import cn from 'clsx'
import { HyperLink, type HyperLinkProps } from '@/components/hyper-link/hyper-link'
import { Text } from '@/components/Text/Text'
import css from './choice-card.module.css'
import { cloneElement, type ReactElement } from 'react'
import { Box } from '@/components/Box/Box'
import { Card } from '@/components/Card/Card'

const backgroundColorMap = {
    red: 'var(--composition-background-red)',
    green: 'var(--composition-background-green)',
    yellow: 'var(--composition-background-yellow)',
    seaSalt: 'var(--composition-background-sea-salt)',
}

export interface ChoiceCardProps extends Omit<HyperLinkProps, 'bold' | 'size'> {
    icon?: ReactElement
    centerText?: boolean
    children: string
    className?: string
    backgroundColor?: ChoiceCardBackgroundColor
}

export type ChoiceCardBackgroundColor = keyof typeof backgroundColorMap

export function ChoiceCard({
    children,
    icon,
    centerText,
    className,
    disabled,
    backgroundColor = 'red',
    ...rest
}: ChoiceCardProps) {
    const clonedIcon = icon
        ? cloneElement(icon, { width: 32, height: 32, color: 'var(--display-onlight-tertiary)' })
        : null

    return (
        <HyperLink
            className={cn(css.cardLink, { [css.disabled]: disabled }, className)}
            title={children?.toString()}
            {...rest}
        >
            <Card className={css.card}>
                {clonedIcon ? (
                    <Box
                        className={css.icon}
                        style={{ '--choice-card-background': backgroundColorMap[backgroundColor] }}
                    >
                        {clonedIcon}
                    </Box>
                ) : null}
                <Box className={cn(css.text, { [css.centerText]: centerText })}>
                    <Text tag="span" size="minor-base" weight="semibold" className={css.copy}>
                        {children}
                    </Text>
                </Box>
            </Card>
        </HyperLink>
    )
}
